<script lang="ts">
  /**
   * Two-factor enrolment (Settings → Security).
   *
   * Three states: off → enrolling (QR + code) → on. The recovery codes are shown exactly
   * once, immediately after confirmation, because the server keeps only their HMACs.
   *
   * The scope note is deliberate and must stay visible: this protects the web session, not
   * IMAP/SMTP, so a user does not believe their mailbox is locked down further than it is.
   */
  import qrcode from 'qrcode-generator';
  import { ApiError } from '$lib/api/client';
  import { api } from '$lib/api/client';
  import { t } from '$lib/i18n';
  import type { TwoFactorStatus } from '$lib/api/types';

  let status = $state<TwoFactorStatus | null>(null);
  let secret = $state('');
  let uri = $state('');
  let code = $state('');
  let recovery = $state<string[]>([]);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let note = $state<string | null>(null);
  let copied = $state(false);
  let disabling = $state(false);

  const enrolling = $derived(!!uri && !status?.enabled);
  const qrSvg = $derived(uri ? qrSvgFor(uri) : '');

  $effect(() => {
    void load();
  });

  async function load(): Promise<void> {
    try {
      status = await api.twofaStatus();
    } catch {
      status = null;
    }
  }

  /** An inline SVG, so nothing is fetched and the CSP stays `default-src 'self'`. */
  function qrSvgFor(text: string): string {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    let path = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (qr.isDark(r, c)) path += `M${c} ${r}h1v1h-1z`;
      }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" role="img" aria-label="QR">`
      + `<rect x="-1" y="-1" width="${n + 2}" height="${n + 2}" fill="#fff"/>`
      + `<path d="${path}" fill="#000"/></svg>`;
  }

  async function begin(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaSetup();
      secret = r.secret;
      uri = r.uri;
      code = '';
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'already_enabled' ? 'twoFactorOn' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function confirm(): Promise<void> {
    busy = true;
    error = null;
    try {
      const r = await api.twofaConfirm(code.trim());
      recovery = r.recovery;
      secret = '';
      uri = '';
      code = '';
      note = t('twoFactorEnabled');
      await load();
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  async function disable(): Promise<void> {
    busy = true;
    error = null;
    try {
      await api.twofaDisable(code.trim());
      note = t('twoFactorDisabled');
      code = '';
      disabling = false;
      recovery = [];
      await load();
    } catch (e) {
      error = t(e instanceof ApiError && e.code === 'bad_code' ? 'twoFactorBadCode' : 'adminFailed');
    } finally {
      busy = false;
    }
  }

  function cancel(): void {
    secret = '';
    uri = '';
    code = '';
    error = null;
    disabling = false;
  }

  async function copyRecovery(): Promise<void> {
    try {
      await navigator.clipboard.writeText(recovery.join('\n'));
      copied = true;
      setTimeout(() => { copied = false; }, 2000);
    } catch {
      /* clipboard blocked: the codes are on screen to copy by hand */
    }
  }
</script>

<section class="security">
  <h3>{t('security')}</h3>
  <div class="card">
    <div class="row">
      <span class="label">{t('twoFactor')}</span>
      <span class="state" class:on={status?.enabled}>{status?.enabled ? t('twoFactorOn') : t('twoFactorOff')}</span>
    </div>
    <p class="tik-hint">{t('twoFactorIntro')}</p>
    <p class="tik-hint scope">{t('twoFactorImapNote')}</p>

    {#if note}<p class="tik-note" role="status">{note}</p>{/if}
    {#if error}<p class="tik-error" role="alert">{error}</p>{/if}

    {#if recovery.length}
      <div class="recovery">
        <strong>{t('twoFactorRecovery')}</strong>
        <p class="tik-hint">{t('twoFactorRecoveryIntro')}</p>
        <ul>
          {#each recovery as c (c)}<li>{c}</li>{/each}
        </ul>
        <div class="tik-actions">
          <button type="button" class="tik-btn" onclick={copyRecovery}>{copied ? t('twoFactorCopied') : t('twoFactorCopy')}</button>
          <button type="button" class="tik-btn primary" onclick={() => (recovery = [])}>{t('twoFactorDone')}</button>
        </div>
      </div>
    {:else if enrolling}
      <div class="enrol">
        <p>{t('twoFactorScan')}</p>
        <!-- eslint-disable-next-line svelte/no-at-html-tags -- locally generated SVG, no user input -->
        <div class="qr">{@html qrSvg}</div>
        <p class="tik-hint">{t('twoFactorManual')}</p>
        <code class="secret" dir="ltr">{secret}</code>
        <label>
          <span>{t('twoFactorCode')}</span>
          <input class="tik-input mono" type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" maxlength="8" bind:value={code} />
        </label>
        <div class="tik-actions">
          <button type="button" class="tik-btn" onclick={cancel}>{t('adminCancel')}</button>
          <button type="button" class="tik-btn primary" disabled={busy || code.trim().length < 6} onclick={confirm}>{t('twoFactorConfirm')}</button>
        </div>
      </div>
    {:else if disabling}
      <div class="enrol">
        <label>
          <span>{t('twoFactorCode')}</span>
          <input class="tik-input mono" type="text" dir="ltr" inputmode="numeric" autocomplete="one-time-code" maxlength="20" bind:value={code} />
        </label>
        <div class="tik-actions">
          <button type="button" class="tik-btn" onclick={cancel}>{t('adminCancel')}</button>
          <button type="button" class="tik-btn danger" disabled={busy || code.trim().length < 6} onclick={disable}>{t('twoFactorDisable')}</button>
        </div>
      </div>
    {:else if status?.enabled}
      <p class="tik-hint">{t('twoFactorRecoveryLeft', { n: status.recoveryLeft })}</p>
      <div class="tik-actions">
        <button type="button" class="tik-btn" onclick={() => { disabling = true; note = null; }}>{t('twoFactorDisable')}</button>
      </div>
    {:else}
      <div class="tik-actions">
        <button type="button" class="tik-btn primary" disabled={busy} onclick={begin}>{t('twoFactorEnable')}</button>
      </div>
    {/if}
  </div>
</section>

<style>

  /* @css settings/TwoFactor start */
  /* Controls come from the shared .tik-* classes in styles/global.css. */
  .card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .label {
    font-weight: 600;
    color: var(--text-strong);
  }
  .state {
    font-size: var(--text-xs, 12px);
    padding: 2px var(--space-3);
    border-radius: var(--radius-pill);
    background: var(--surface-sunken);
    color: var(--text-muted);
  }
  .state.on {
    background: var(--success-100);
    color: var(--success-500);
  }
  /* The scope note has a rule beside it because it limits what the feature protects. */
  .scope {
    border-inline-start: var(--rule-accent-width, 3px) solid var(--border-strong);
    padding-inline-start: var(--space-3);
  }
  .enrol,
  .recovery {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .enrol > p,
  .recovery > strong {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.7;
    color: var(--text-body);
  }
  .enrol label {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-strong);
  }
  .qr {
    inline-size: 200px;
    max-inline-size: 100%;
    align-self: center;
    background: #fff;
    padding: var(--space-2);
    border-radius: var(--radius-control);
  }
  .qr :global(svg) {
    display: block;
    inline-size: 100%;
    block-size: auto;
  }
  .secret {
    align-self: center;
    font-family: var(--font-mono);
    letter-spacing: 2px;
    background: var(--surface-sunken);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-control);
    user-select: all;
  }
  .recovery ul {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: var(--space-2);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .recovery li {
    font-family: var(--font-mono);
    background: var(--surface-sunken);
    border-radius: var(--radius-control);
    padding: var(--space-2);
    text-align: center;
    direction: ltr;
  }
  /* @css settings/TwoFactor end */
</style>
